import { sendEmailVerification, type ActionCodeSettings, type User } from 'firebase/auth'

/**
 * One place that decides what a verification link looks like.
 *
 * Every "send" and "resend" in the app has to agree, because Firebase invalidates the
 * previous code the moment a new one is issued: the older mail still sitting in the inbox
 * goes dead, and clicking it reports `auth/invalid-action-code`. Call sites that skipped
 * the settings also dropped people on Firebase's default continue URL instead of back into
 * registration.
 */
export function useEmailVerification() {
    /**
     * @param returnTo Where the person should end up once the address is confirmed.
     *                 Defaults to the page they are on when the mail is sent.
     */
    function verificationActionCodeSettings(returnTo?: string): ActionCodeSettings {
        const baseUrl = import.meta.client ? window.location.origin : 'https://growni.sk'

        const currentPath = import.meta.client
            ? window.location.pathname + window.location.search + window.location.hash
            : '/'

        // Route through /auth/registration so new users always fill in name/rules/livingAbroad.
        // registration.vue auto-forwards users who already have firstName (e.g. returning users).
        const registrationPath = `/auth/registration?redirect=${encodeURIComponent(returnTo || currentPath)}`

        return {
            url: `${baseUrl}/auth/action/verify-email?redirect=${encodeURIComponent(registrationPath)}`,
            handleCodeInApp: false
        }
    }

    /**
     * Sends a fresh verification mail. Remember that this kills any link the person already
     * has - tell them to open the newest mail rather than to retry an older one.
     */
    async function sendVerificationEmail(user: User, returnTo?: string): Promise<void> {
        await sendEmailVerification(user, verificationActionCodeSettings(returnTo))
    }

    return {
        verificationActionCodeSettings,
        sendVerificationEmail
    }
}
